// ONE source of truth for how LIVE-ACCOUNT money is labelled across the whole app. The live book is
// the user's broker account in ITS base currency (€/£/…), so every figure derived from it must carry
// that symbol — not a hardcoded "$". The account context (account.jsx) calls setAccountCurrency()
// whenever the selected account changes, so trade confirms, toasts, modals, and pages all agree.
//
// NOTE: MODEL / backtest money (Designer projections) is a DIFFERENT currency (USD) and keeps its
// own formatter — never use these helpers for model values.
export const MINUS = "−";
const SYM = { USD: "$", EUR: "€", GBP: "£", CHF: "CHF ", JPY: "¥", CAD: "C$", AUD: "A$", SEK: "kr ", PLN: "zł ", NOK: "kr ", DKK: "kr ", HKD: "HK$", SGD: "S$" };

// Initialise from the LAST-KNOWN display ccy (persisted) so a reload doesn't briefly render the wrong
// symbol before account.jsx's async fetch resolves — that race showed the Account chart in "$" while the
// EUR hero showed "€" (docs/design/UI_CONSISTENCY_REVIEW.md C6). Falls back to USD only on first-ever load.
let _code = (() => { try { return localStorage.getItem("helm.ccy") || "USD"; } catch { return "USD"; } })();
export const setAccountCurrency = (code) => {
  if (!code || code === _code) return;
  _code = code;
  try { localStorage.setItem("helm.ccy", code); } catch { /* private mode / no storage */ }
};
export const curCode = () => _code;
export const curSym = () => SYM[_code] || (_code ? _code + " " : "$");
// Symbol for ANY currency code (not just the active account ccy) — used by <Money> to render a
// Money object in its OWN currency (e.g. a USD short shown natively while the account is EUR).
// F6 (W-17): an UNKNOWN currency gets no symbol (the caller labels it "currency unknown"), never "$".
export const symFor = (code) => (code ? (SYM[code] || String(code).toUpperCase() + " ") : "");

// Treat a non-finite numeric (NaN / ±Infinity) the SAME as null — otherwise a NaN slips through to
// render "$NaN" / "NaN%". `nf(x)` is true when x should fall to the empty branch. The empty-branch
// return value below is unchanged ("—"), so existing tests/behavior on the null path stay identical.
const nf = (x) => x == null || !Number.isFinite(Number(x));
export const money = (x) => (nf(x) ? "—" : curSym() + Math.round(x).toLocaleString("en-US"));
export const money2 = (x) => (nf(x) ? "—" : curSym() + Number(x).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const signedMoney = (x) => (nf(x) ? "—" : (x < 0 ? MINUS : "+") + curSym() + Math.round(Math.abs(x)).toLocaleString("en-US"));
// signed percent with the true minus glyph — THE one definition (BookHero, portfolioViz re-export)
export const signedPct = (x) => (x == null ? "" : `${x > 0 ? "+" : x < 0 ? MINUS : ""}${Math.abs(x).toFixed(1)}%`);
// compact axis label ($1.7k / $2.3M) — currency-aware
export const fmtMoney = (v) => (Math.abs(v) >= 1e6 ? curSym() + (v / 1e6).toFixed(2) + "M" : Math.abs(v) >= 1e3 ? curSym() + (v / 1e3).toFixed(1) + "k" : curSym() + Math.round(v));
export const fmtMoneyFull = (v) => (v < 0 ? MINUS : "") + curSym() + Math.round(Math.abs(v)).toLocaleString("en-US");
